import React, { useState, useEffect } from "react";

const OrderSection = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [quantity, setQuantity] = useState(500);
    const [selectedSize, setSelectedSize] = useState("500ml");
    const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Tiered business pricing based on order size units (INR ₹)
    const getUnitPrice = (qty) => {
        if (qty >= 5000) return 18;  // Tier 3: High-volume bulk
        if (qty >= 1000) return 22;  // Tier 2: Mid-range enterprise
        return 26;                   // Tier 1: Minimum standard order
    };

    const unitPrice = getUnitPrice(quantity);
    const totalPrice = quantity * unitPrice;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
    };

    const styles = {
        wrapper: {
            minHeight: "100vh",
            width: "100%",
            background: "#2c3437",
            backgroundImage: "url('https://transparenttextures.com')",
            backgroundRepeat: "repeat",
            backgroundBlendMode: "multiply",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            fontFamily: "'Montserrat', sans-serif",
            color: "white",
            padding: isMobile ? "80px 20px" : "120px 40px",
            boxSizing: "border-box",
            position: "relative",
            overflow: "hidden",
        },
        container: {
            width: "100%",
            maxWidth: "1100px",
            zIndex: 2,
            position: "relative",
        },
        headerSection: {
            textAlign: "center",
            marginBottom: isMobile ? "40px" : "60px",
        },
        tagline: {
            color: "#6ec1e4",
            letterSpacing: "3px",
            fontSize: isMobile ? "12px" : "14px",
            fontWeight: "600",
            textTransform: "uppercase",
            marginBottom: "12px",
        },
        mainTitle: {
            fontSize: isMobile ? "30px" : "44px",
            fontWeight: "900",
            letterSpacing: "1px",
            lineHeight: 1.2,
            color: "#ffffff",
            margin: 0,
        },
        splitLayout: {
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "40px",
            alignItems: "stretch",
        },
        calculatorPane: {
            flex: 1,
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "24px",
            padding: isMobile ? "24px" : "40px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
        },
        formPane: {
            flex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.04)",
            borderRadius: "24px",
            padding: isMobile ? "24px" : "40px",
        },
        sectionHeading: {
            fontSize: "20px",
            fontWeight: "800",
            letterSpacing: "1px",
            marginBottom: "24px",
            color: "#ffffff",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            paddingBottom: "12px",
        },
        label: {
            fontSize: "12px",
            fontWeight: "600",
            letterSpacing: "1.5px",
            color: "#6ec1e4",
            textTransform: "uppercase",
            display: "block",
            marginBottom: "10px",
        },
        sizeGrid: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "12px",
            marginBottom: "30px",
        },
        sizeButton: (isSelected) => ({
            background: isSelected ? "#6ec1e4" : "rgba(255,255,255,0.03)",
            border: isSelected ? "1px solid #6ec1e4" : "1px solid rgba(255,255,255,0.1)",
            color: isSelected ? "#2c3437" : "#ffffff",
            padding: "12px",
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            transition: "all 0.3s ease",
            textAlign: "center",
            outline: "none",
        }),
        rangeSlider: {
            width: "100%",
            accentColor: "#6ec1e4",
            margin: "15px 0 30px 0",
            cursor: "pointer",
        },
        invoiceRow: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 0",
            borderBottom: "1px dashed rgba(255,255,255,0.1)",
        },
        input: {
            width: "100%",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "12px",
            padding: "14px",
            color: "#ffffff",
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "14px",
            marginBottom: "20px",
            boxSizing: "border-box",
            outline: "none",
            transition: "border-color 0.3s ease",
        },
        submitBtn: {
            width: "100%",
            background: "#6ec1e4",
            color: "#2c3437",
            border: "none",
            borderRadius: "12px",
            padding: "16px",
            fontSize: "14px",
            fontWeight: "700",
            letterSpacing: "1px",
            textTransform: "uppercase",
            cursor: "pointer",
            boxShadow: "0px 8px 20px rgba(110,193,228,0.2)",
            transition: "all 0.3s ease",
        }
    };

    return (
        <div id="order" style={styles.wrapper}>
            <div style={styles.container}>

                {/* Screen Branding Title */}
                <div style={styles.headerSection}>
                    <div style={styles.tagline}>Procurement Engine</div>
                    <h2 style={styles.mainTitle}>Bulk Wholesale Operations</h2>
                </div>

                <div style={styles.splitLayout}>

                    {/* Left Pane: Configurator & Price Calculator */}
                    <div style={styles.calculatorPane}>
                        <div>
                            <h3 style={styles.sectionHeading}>1. Setup Matrix</h3>

                            <span style={styles.label}>Select Cell Capacity</span>
                            <div style={styles.sizeGrid}>
                                {["250ml", "500ml", "1000ml"].map((size) => (
                                    <button
                                        key={size}
                                        type="button"
                                        onClick={() => setSelectedSize(size)}
                                        style={styles.sizeButton(selectedSize === size)}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>

                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span style={styles.label}>Target Batch Volume</span>
                                <span style={{ fontWeight: "700", color: "#ffffff", fontSize: "14px" }}>
                                    {quantity.toLocaleString()} Units
                                </span>
                            </div>
                            <input
                                type="range"
                                min="200"
                                max="10000"
                                step="100"
                                value={quantity}
                                onChange={(e) => setQuantity(Number(e.target.value))}
                                style={styles.rangeSlider}
                            />
                        </div>

                        <div>
                            <h3 style={styles.sectionHeading}>2. Commercial Estimate</h3>
                            <div style={styles.invoiceRow}>
                                <span style={{ color: "#b3bfc4", fontSize: "14px" }}>Base Unit Rate</span>
                                <span style={{ fontFamily: "monospace", fontWeight: "700" }}>₹{unitPrice}.00</span>
                            </div>
                            <div style={styles.invoiceRow}>
                                <span style={{ color: "#b3bfc4", fontSize: "14px" }}>Production Tier</span>
                                <span style={{ color: "#6ec1e4", fontSize: "12px", fontWeight: "700" }}>
                                    {quantity >= 5000 ? "HIGH BULK DISCOUNT" : quantity >= 1000 ? "ENTERPRISE BATCH" : "STANDARD CONTRACT"}
                                </span>
                            </div>
                            <div style={{ ...styles.invoiceRow, borderBottom: "none", paddingTop: "20px" }}>
                                <span style={{ fontWeight: "700", fontSize: "16px" }}>Total Commercial Quote</span>
                                <span style={{ fontSize: "22px", fontWeight: "900", color: "#6ec1e4", fontFamily: "monospace" }}>
                                    ₹{totalPrice.toLocaleString()}.00
                                </span>
                            </div>
                            <span
                                style={{
                                    fontSize: "10px",
                                    color: "#9ca3af",
                                    display: "block",
                                    marginTop: "5px",
                                    textAlign: "right"
                                }}
                            >
                                *Excluding regional GST & freight logistics costs
                            </span>
                        </div>
                    </div>

                    {/* Right Pane: Wholesale Inquiry Communication Form */}
                    <div style={styles.formPane}>
                        <h3 style={styles.sectionHeading}>
                            3. Request Secure Manifest
                        </h3>

                        {submitted ? (
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: "40px 0"
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "48px",
                                        marginBottom: "20px"
                                    }}
                                >
                                    📥
                                </div>

                                <h4
                                    style={{
                                        fontSize: "20px",
                                        fontWeight: "700",
                                        marginBottom: "10px"
                                    }}
                                >
                                    Inquiry Manifest Recorded
                                </h4>

                                <p
                                    style={{
                                        color: "#b3bfc4",
                                        fontSize: "14px",
                                        lineHeight: "1.6"
                                    }}
                                >
                                    Our industrial unit plant manager in India
                                    will review your custom mold parameters and
                                    reach out within 4 business hours.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Corporate Contact Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    style={styles.input}
                                    required
                                />

                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Official Business Email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    style={styles.input}
                                    required
                                />

                                <input
                                    type="text"
                                    name="company"
                                    placeholder="Enterprise / Brand Entity"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    style={styles.input}
                                />

                                <textarea
                                    name="message"
                                    rows="3"
                                    placeholder="Specify logo placement, distribution timeline, or regional delivery location requirements..."
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    style={{
                                        ...styles.input,
                                        resize: "none",
                                        height: "90px"
                                    }}
                                />

                                <button
                                    type="submit"
                                    style={styles.submitBtn}
                                >
                                    Generate Formal Factory Contract
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSection;